import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Home, MapPin, Plus, X } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { ContainerTreePanel } from "@/components/storage/ContainerTreePanel";
import { generateId, useApp, type Location } from "@/context/AppContext";
import { RequireRole } from "@/lib/auth";

export const Route = createFileRoute("/tools/storage-bins")({
  component: StorageBinsPage,
});

/**
 * Storage management (TNK-191). The container tree is the single surface for
 * bins now — bins are bin-kind containers, created/nested via the tree. The
 * legacy flat bin list/form was removed when bins collapsed into containers.
 * Locations (sites) are still managed via the dialog; they mirror into the tree
 * as site nodes.
 */
function StorageBinsPage() {
  const {
    storageBins,
    locations,
    addLocation,
    updateLocation,
    archiveLocation,
    restoreLocation,
  } = useApp();

  const [manageLocationsOpen, setManageLocationsOpen] = useState(false);

  const activeLocations = useMemo(
    () => locations.filter((l) => !l.archivedAt),
    [locations],
  );

  // Bin counts per location (derived bins → locationId) for the dialog.
  const binCountByLocation = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of storageBins) {
      const id = b.locationId ?? "";
      m.set(id, (m.get(id) ?? 0) + 1);
    }
    return m;
  }, [storageBins]);

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
        <RequireRole min="manager">
          <button
            type="button"
            onClick={() => setManageLocationsOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
            title="Manage locations"
          >
            <MapPin className="h-3.5 w-3.5" />
            Locations
            {locations.length > 1 && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {activeLocations.length}
              </span>
            )}
          </button>
        </RequireRole>
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Storage</h1>
        <p className="text-sm text-muted-foreground">
          Organize where your cards live as a tree — sites, cases, shelves,
          bins, boxes, and tabs. Create or nest containers with the &ldquo;+
          inside&rdquo; buttons. Renaming or deleting a bin updates all assigned
          cards.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        <ContainerTreePanel />
      </div>

      {manageLocationsOpen && (
        <ManageLocationsDialog
          locations={locations}
          binCountByLocation={binCountByLocation}
          onClose={() => setManageLocationsOpen(false)}
          onAdd={(name) =>
            addLocation({
              id: generateId("LOC"),
              name,
              kind: "storage",
              createdAt: new Date().toISOString(),
            })
          }
          onRename={(loc, name) => updateLocation({ ...loc, name })}
          onArchive={(id) => archiveLocation(id)}
          onRestore={(id) => restoreLocation(id)}
        />
      )}
    </section>
  );
}

interface ManageLocationsDialogProps {
  locations: Location[];
  binCountByLocation: Map<string, number>;
  onClose: () => void;
  onAdd: (name: string) => void;
  onRename: (loc: Location, name: string) => void;
  onArchive: (id: string) => Promise<{ error?: string }>;
  onRestore: (id: string) => Promise<{ error?: string }>;
}

/**
 * Lightweight modal for adding / renaming / archiving Locations (TNK-103
 * Phase G). The "Home" location stays unarchivable since it's the seed
 * fallback every bin defaults to.
 */
function ManageLocationsDialog({
  locations,
  binCountByLocation,
  onClose,
  onAdd,
  onRename,
  onArchive,
  onRestore,
}: ManageLocationsDialogProps) {
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setError("Name required");
      return;
    }
    if (
      locations.some(
        (l) => !l.archivedAt && l.name.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      setError("A location with that name already exists");
      return;
    }
    setError(null);
    onAdd(trimmed);
    setNewName("");
  };

  const startRename = (loc: Location) => {
    setRenamingId(loc.id);
    setRenameValue(loc.name);
  };
  const commitRename = (loc: Location) => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== loc.name) onRename(loc, trimmed);
    setRenamingId(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-md overflow-hidden rounded-t-lg bg-background shadow-lg sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-3">
          <div className="text-sm font-semibold text-foreground">
            Manage Locations
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-2 p-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setError(null);
              }}
              placeholder="New location (e.g. Shop — Main St)"
              className="flex-1 rounded-md bg-muted px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground"
            />
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </div>
          {error && <div className="text-[11px] text-destructive">{error}</div>}
        </div>

        <div className="max-h-[50vh] overflow-y-auto border-t border-border">
          {locations.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">
              No locations yet.
            </div>
          ) : (
            locations.map((loc) => {
              const binCount = binCountByLocation.get(loc.id) ?? 0;
              const isHome = loc.name === "Home";
              const isRenaming = renamingId === loc.id;
              return (
                <div
                  key={loc.id}
                  className={`flex items-center gap-2 border-b border-border/50 px-3 py-2 ${
                    loc.archivedAt ? "opacity-60" : ""
                  }`}
                >
                  {isHome ? (
                    <Home className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  {isRenaming ? (
                    <input
                      autoFocus
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => commitRename(loc)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitRename(loc);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                      className="flex-1 rounded bg-muted px-2 py-1 text-sm outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => !loc.archivedAt && startRename(loc)}
                      disabled={!!loc.archivedAt}
                      className="min-w-0 flex-1 truncate text-left text-sm font-medium text-foreground hover:text-primary disabled:cursor-default disabled:hover:text-foreground"
                      title={loc.archivedAt ? "Archived" : "Click to rename"}
                    >
                      {loc.name}
                      {loc.archivedAt && (
                        <span className="ml-2 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-700">
                          archived
                        </span>
                      )}
                    </button>
                  )}
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {binCount} {binCount === 1 ? "bin" : "bins"}
                  </span>
                  {loc.archivedAt ? (
                    <button
                      type="button"
                      onClick={() => void onRestore(loc.id)}
                      className="shrink-0 rounded-md bg-emerald-500/15 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-500/25"
                    >
                      Restore
                    </button>
                  ) : (
                    !isHome && (
                      <button
                        type="button"
                        onClick={() => void onArchive(loc.id)}
                        className="shrink-0 rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted/70"
                        title="Archive"
                      >
                        Archive
                      </button>
                    )
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
